import React, { useEffect, useMemo, useRef, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import Flatpickr from "react-flatpickr";
import { Link } from "react-router-dom";
import { Dropdown } from "Common/Components/Dropdown";

// Icon
import {
  MoreHorizontal,
  Eye,
  FileEdit,
  Trash2,
  Search,
  Plus,
} from "lucide-react";

import DeleteModal from "Common/DeleteModal";

// react-redux
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import {
  getDiamonds as onGetDiamonds,
  deleteDiamond as onDeleteDiamond,
  getDiamondByStockId as onGetDiamondByStockId,
} from "slices/thunk";
import { ToastContainer } from "react-toastify";
import filterDataBySearch from "Common/filterDataBySearch";
import DiamondsTable from "./DiamondsTable";
import { setCurrentPage, setIsLoading } from "slices/diamond/reducer";
import noUiSlider from "nouislider";
import "./Diamonds.css";
import {
  carateSliderConfig,
  colorSliderConfig,
  getClaritySliderConfig,
  getCutSliderConfig,
  getSelectedValues,
  priceSliderConfig,
} from "utils/diamond";

const Diamonds = () => {
  const dispatch = useDispatch<any>();
  const sliderRef = useRef<HTMLDivElement>(null);
  const priceSliderRef = useRef<HTMLDivElement>(null);
  const caratSliderRef = useRef<HTMLDivElement>(null);
  const cutSliderRef = useRef<HTMLDivElement>(null);
  const colorSliderRef = useRef<HTMLDivElement>(null);
  const claritySliderRef = useRef<HTMLDivElement>(null);

  const selectDataList = createSelector(
    (state: any) => state.Diamond,
    (state) => ({
      dataList: state.productList,
      diamonds: state.diamonds,
      currentPage: state.currentPage,
      totalDiamonds: state.totalDiamonds,
      isLoading: state.isLoading,
    })
  );

  const { dataList, diamonds, currentPage, totalDiamonds, isLoading } =
    useSelector(selectDataList);

  const [search, setSearch] = useState<string>("");
  const [data, setData] = useState<any>([]);
  const [price, setPrice] = useState<Number[]>([]);
  const [carat, setCarat] = useState<Number[]>([]);
  const [color, setColor] = useState<Number[]>([]);
  const [cut, setCut] = useState<Number[]>([]);
  const [clarity, setClarity] = useState<Number[]>([]);
  const [diamondData, setDiamondData] = useState<any>();

  useEffect(() => {
    setData(diamonds);
  }, [diamonds]);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  // Delete Data
  const onClickDelete = (diamond: any) => {
    setDeleteModal(true);
    if (diamond?._id) {
      setDiamondData(diamond);
    }
  };

  const handleDelete = () => {
    if (diamondData) {
      dispatch(onDeleteDiamond(diamondData._id));
      setDeleteModal(false);
    }
  };

  // Search Data
  const filterSearchData = (e: any) => {
    const search = e.target.value;
    setSearch(search);
    const keysToSearch = ["productCode", "productName", "category", "status"];
    filterDataBySearch(dataList, search, keysToSearch, setData);
  };

  const columns = useMemo(
    () => [
      {
        header: "Stock Id",
        accessorKey: "stockId",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Link
            to="#"
            className="transition-all duration-150 ease-linear product_code text-custom-500 hover:text-custom-600"
          >
            {cell.getValue()}
          </Link>
        ),
      },
      {
        header: "Certificate",
        accessorKey: "certificate",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Link
            to="#"
            className="transition-all duration-150 ease-linear product_code text-custom-500 hover:text-custom-600"
          >
            {cell.getValue()}
          </Link>
        ),
      },
      {
        header: "Shape",
        accessorKey: "shape",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <span className="category px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-slate-100 border-slate-200 text-slate-500 dark:bg-slate-500/20 dark:border-slate-500/20 dark:text-zink-200">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Carat",
        accessorKey: "weight",
        enableColumnFilter: false,
      },
      {
        header: "Color",
        accessorKey: "color",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Clarity",
        accessorKey: "clarity",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Cut",
        accessorKey: "cut",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Polish",
        accessorKey: "polish",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Symmetry",
        accessorKey: "symmetry",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Depth",
        accessorKey: "depth",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Table",
        accessorKey: "table",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Lab",
        accessorKey: "lab",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <span className="category px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-slate-100 border-slate-200 text-slate-500 dark:bg-slate-500/20 dark:border-slate-500/20 dark:text-zink-200">
            {cell.getValue()}
          </span>
        ),
      },
      // {
      //   header: "Supplier",
      //   accessorKey: "supplierId",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: (cell: any) => {
      //     const supplierName = cell.row.original.supplierId;
      //     return supplierName;
      //   },
      // },
      // {
      //   header: "Inventory",
      //   accessorKey: "inventoryId",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      {
        header: "Price",
        accessorKey: "priceWithProfit",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => <span>{Number(cell.getValue()).toFixed(2)}</span>,
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <Dropdown className="relative dropdown">
            <Dropdown.Trigger
              className="flex items-center justify-center size-[30px] dropdown-toggle p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20"
              id="productAction1"
              data-bs-toggle="dropdown"
            >
              <MoreHorizontal className="size-3" />
            </Dropdown.Trigger>
            <Dropdown.Content
              placement={cell.row.index ? "top-end" : "right-end"}
              className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md dropdown-menu min-w-[10rem] dark:bg-zink-600"
              aria-labelledby="productAction1"
            >
              <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to={`/diamond/${cell.row.original._id}`}
                >
                  <Eye className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Overview</span>
                </Link>
              </li>
              {/* <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to="/apps-ecommerce-product-create"
                >
                  <FileEdit className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Edit</span>
                </Link>
              </li> */}
              <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to="#!"
                  onClick={() => {
                    const data = cell.row.original;
                    onClickDelete(data);
                  }}
                >
                  <Trash2 className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Delete</span>
                </Link>
              </li>
            </Dropdown.Content>
          </Dropdown>
        ),
      },
    ],
    []
  );

  const handlePagination = async (page: number) => {
    dispatch(setCurrentPage(page as any));
    dispatch(setIsLoading(true));
    await dispatch(onGetDiamonds({ page: page || 1 }));
    dispatch(setIsLoading(false));
  };

  const handleGetDiamonds = async () => {
    dispatch(setIsLoading(true));
    await dispatch(onGetDiamonds({ page: currentPage }));
    dispatch(setIsLoading(false));
  };

  useEffect(() => {
    handleGetDiamonds();
  }, [dispatch]);



  const cutMarks = {
    10: "Good",
    20: "Very Good",
    30: "Excellent",
    40: "Ideal",
  };

  const clarityMarks = {
    10: "IF",
    20: "VVS1",
    30: "VVS2",
    40: "VS1",
    50: "VS2",
    60: "SI1",
    70: "SI2",
    80: "SI3",
    90: "I1",
    100: "I2",
    110: "I3",
  };

  const colorMarks = {
    10: "D",
    20: "E",
    30: "F",
    40: "G",
    50: "H",
    60: "I",
    70: "J",
    80: "K",
    90: "L",
  };

  useEffect(() => {

    initializeSliders();
  }, []);

  const initializeSliders = () => {
    if (priceSliderRef.current) {
      let config = priceSliderConfig;
      if (price.length) {
        config.start = price as number[];
      }
      const priceSlider = noUiSlider.create(priceSliderRef.current, config);
      priceSlider.on("end", function (values: any) {
        setPrice([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "price", [
          Number(values[0]),
          Number(values[1]),
        ]);
      });
      priceSlider.on("set", function (values: any) {
        setPrice([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "price", [
          Number(values[0]),
          Number(values[1]),
        ]);
      });
    }
    if (caratSliderRef.current) {
      let config = carateSliderConfig;
      if (carat.length) {
        config.start = carat as number[];
      }
      const caratSlider = noUiSlider.create(caratSliderRef.current, config);
      caratSlider.on("end", (values: (string | number)[]) => {
        setCarat([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "carat", [
          Number(values[0]),
          Number(values[1]),
        ]);
      });
      caratSlider.on("set", (values: (string | number)[]) => {
        setCarat([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "carat", [
          Number(values[0]),
          Number(values[1]),
        ]);
      });
    }
    if (colorSliderRef.current) {
      let config = colorSliderConfig;
      if (color.length) {
        config.start = color as number[];
      }
      const colorSlider = noUiSlider.create(
        colorSliderRef.current,
        //@ts-ignore
        config
      );
      colorSlider.on("end", (values: any) => {
        setColor([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "color", [
          Number(values[0]),
          Number(values[1]),
        ]);
      });
      colorSlider.on("set", (values: any) => {
        setColor([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "color", [
          Number(values[0]),
          Number(values[1]),
        ]);
      });
    }
    if (cutSliderRef.current) {
      let config = getCutSliderConfig();
      if (cut.length) {
        config.start = cut as number[];
      }
      const cutSlider = noUiSlider.create(
        cutSliderRef.current,
        //@ts-ignore
        config
      );
      cutSlider.on("end", (values: any) => {
        setCut([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "cut", [Number(values[0]), Number(values[1])]);
      });
      cutSlider.on("set", (values: any) => {
        setCut([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "cut", [Number(values[0]), Number(values[1])]);
      });
    }
    if (claritySliderRef.current) {
      let config = getClaritySliderConfig();
      if (clarity.length) {
        config.start = clarity as number[];
      }
      const claritySlider = noUiSlider.create(
        claritySliderRef.current,
        //@ts-ignore
        config
      );
      claritySlider.on("end", (values: (Number | String)[]) => {
        setClarity([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "clarity", [
          Number(values[0]),
          Number(values[1]),
        ]);
      });
      claritySlider.on("set", (values: (Number | String)[]) => {
        setClarity([Number(values[0]), Number(values[1])]);
        handleFilterDiamonds(1, "clarity", [
          Number(values[0]),
          Number(values[1]),
        ]);
      });
    }
  };

  const handleFilterDiamonds = (
    page: Number,
    type: string,
    values: Number[]
  ) => {
    let payload: any = { page };

    if (type === "price") {
      payload = {
        ...payload,
        minPrice: values[0],
        maxPrice: values[1],
      };
    } else if (price.length) {
      payload = {
        ...payload,
        minPrice: price[0],
        maxPrice: price[1],
      };
    }

    if (type === "carat") {
      payload = {
        ...payload,
        minWeight: Number(values[0]),
        maxWeight: Number(values[1]),
      };
    } else if (carat.length) {
      payload = {
        ...payload,
        minWeight: carat[0],
        maxWeight: carat[1],
      };
    }

    if (type === "color") {
      payload["color"] = getSelectedValues(values, colorMarks);
    } else if (color.length) {
      payload["color"] = getSelectedValues(color, colorMarks);
    }

    if (type === "cut") {
      payload["cut"] = getSelectedValues(values, cutMarks).map((cut) =>
        cut.toUpperCase()
      );
    } else if (cut.length) {
      payload["cut"] = getSelectedValues(cut, cutMarks).map((cut) =>
        cut.toUpperCase()
      );
    }

    if (type === "clarity") {
      payload["clarity"] = getSelectedValues(values, clarityMarks);
    } else if (clarity.length) {
      payload["clarity"] = getSelectedValues(clarity, clarityMarks);
    }

    dispatch(onGetDiamonds(payload));
  };

  function handleKeyPress(event: any) {
    if (event.key === "Enter") {
      // Call your function here
      // For example:
      dispatch(onGetDiamondByStockId(event.target.value));
    }
  }

  return (
    <React.Fragment>
      <BreadCrumb title="Diamonds" pageTitle="Products" />
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
      <ToastContainer closeButton={false} limit={1} />
      <div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-8 lg:grid-cols-8 xl:grid-cols-8 2xl:grid-cols-12 my-10 px-5">
          <div className="col-span-4 pr-2.5 my-5 flex items-center ">
            <h5>Price</h5>
            <div className="block w-full pl-5">
              <div className="slider-wrapper " ref={priceSliderRef}></div>
            </div>
          </div>
          <div className="col-span-4 pr-2.5 my-5 flex items-center ">
            <h5>Carat</h5>
            <div className="block w-full pl-5">
              <div className="slider-wrapper " ref={caratSliderRef}></div>
            </div>
          </div>
          <div className="col-span-4 pr-2.5 my-5 flex items-center ">
            <h5>Color</h5>
            <div className="block w-full pl-5">
              <div className="slider-wrapper " ref={colorSliderRef}></div>
            </div>
          </div>
          <div className="col-span-4 pr-2.5 my-5 flex items-center ">
            <h5>Cut</h5>
            <div className="block w-full pl-5">
              <div className="slider-wrapper " ref={cutSliderRef}></div>
            </div>
          </div>
          <div className="col-span-4 pr-2.5 my-5 flex items-center ">
            <h5>Clarity</h5>
            <div className="block w-full pl-5">
              <div className="slider-wrapper " ref={claritySliderRef}></div>
            </div>
          </div>
        </div>
        <div className="card" id="diamondListTable">
          <div className="card-body">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-12">
              <div className="xl:col-span-3">
                <div className="relative">
                  <input
                    type="text"
                    className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="Search for ..."
                    autoComplete="off"
                    onChange={(e) => filterSearchData(e)}
                    value={search}
                    onKeyPress={(e) => handleKeyPress(e)}
                  />
                  <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                </div>
              </div>

              {/* <div className="lg:col-span-2 ltr:lg:text-right rtl:lg:text-left xl:col-span-2 xl:col-start-11">
              <Link
                to="/add-inventory"
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                <Plus className="inline-block size-4" />{" "}
                <span className="align-middle">Add Diamonds</span>
              </Link>
            </div> */}
            </div>
          </div>
          <div className="!pt-1 card-body">
            {data && data.length > 0 ? (
              <DiamondsTable
                isPagination={true}
                columns={columns || []}
                data={data || []}
                customPageSize={10}
                divclassName="overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
                // pageCount={20}
                handlePagination={handlePagination}
                // handleNextPage={handleNextPage}
              />
            ) : (
              <div className="noresult">
                <div className="py-6 text-center">
                  <Search className="size-6 mx-auto mb-3 text-sky-500 fill-sky-100 dark:fill-sky-500/20" />
                  <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                  <p className="mb-0 text-slate-500 dark:text-zink-200">
                    We've searched more than 199+ product We did not find any
                    product for you search.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Diamonds;
